import { useCallback, useEffect, useState } from 'react'
import { Button, Form, Input, InputNumber, Modal, Select, Space, TableProps } from 'antd'
import { EditOutlined, SearchOutlined, UndoOutlined } from '@ant-design/icons'
import CustomTable from '../../components/table'
import defaultParams from '../../assets/constants/default_params'
import { useLoading } from '../../context/LoadingContext'
import { apiGetListCompanies, apiGetListSimInventory, updateSimInventory } from '../../api'
import ButtonBack from '../../components/button/ButtonBack'
import { useSearchParams } from 'react-router-dom'
import { mbhStatusOptions, simTypeOptions } from '../../assets/constants/default_options'
import { filterOptionByLabel } from '../../utils/filter-utils'
import { renderStatus } from '../quanlytopup'
import { ICompany } from '../../models/company.modal'
import { toastError, toastSuccess } from '../../utils/toast'
import { authSelector } from '../../redux/slices/authSlice'
import { useSelector } from 'react-redux'

export default function SimExportOrderDetail() {
    const [companyOptions, setCompanyOptions] = useState<{ label: string, value: Number }[]>([])
    const [simInventory, setSimInventory] = useState<any>()
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [search, setSearch] = useState<any>({})
    const [modalUpdate, setModalUpdate] = useState<{ isOpen: boolean, data: any }>({ isOpen: false, data: null })

    const { setIsLoading } = useLoading()
    const [formSearch] = Form.useForm()
    const [formUpdate] = Form.useForm()
    const { account } = useSelector(authSelector)
    const [searchParams] = useSearchParams()
    const orderId = searchParams.get('orderId')

    const columns: TableProps<any>['columns'] = [
        {
            key: 'id',
            title: 'ID',
            dataIndex: 'id',
        },
        {
            key: 'start_sim',
            title: 'Serial Sim bắt đầu',
            dataIndex: 'start_sim',
            render: start_sim => start_sim.toString().padStart(5, '0'),
        },
        {
            key: 'end_sim',
            title: 'Serial Sim kết thúc',
            dataIndex: 'end_sim',
            render: end_sim => end_sim.toString().padStart(5, '0'),
        },
        {
            key: 'total_sim',
            title: 'Tổng số sim',
            dataIndex: 'total_sim',
        },
        {
            key: 'total_active',
            title: 'Tổng kích hoạt',
            dataIndex: 'total_active',
        },
        {
            key: 'description',
            title: 'Mô tả',
            dataIndex: 'description',
        },
        {
            key: 'status',
            title: 'Trạng thái',
            dataIndex: 'status',
            render: status => renderStatus(status)
        },
        account?.canUpdateSimInventory ? {
            key: 'update',
            title: 'Cập nhật',
            render: data => (
                <Button
                    onClick={() => handleOpenModalUpdate(data)}
                    icon={<EditOutlined />}
                    style={{ background: '#ff9800', color: 'white' }}
                />
            ),
        } : {},
    ]

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true)
            const { data } = await apiGetListSimInventory({
                orderId: orderId,
                status: search.status,
                page: currentPage,
                size: pageSize,
                orderBy: 'start_sim',
            })
            setSimInventory(data)
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, currentPage, pageSize, orderId, search])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const fetchCompanies = useCallback(async () => {
        try {
            const { data } = await apiGetListCompanies({ limit: defaultParams.MAX_LIMIT })
            const options = data.companies?.filter((item: ICompany) => item.supportChannels?.includes('INVENTORY')).map((item: ICompany) => ({
                label: item.name,
                value: item.code
            }))
            setCompanyOptions(options)
        } catch (error) {
            console.log(error)
        }
    }, [])

    useEffect(() => {
        fetchCompanies()
    }, [fetchCompanies])

    const handleSearch = ({ status }: any) => {
        if (status) {
            setSearch({ status })
            setCurrentPage(defaultParams.PAGE)
            setPageSize(defaultParams.LIMIT)
        }
    }

    const handleClearSearch = () => {
        formSearch.resetFields()
        setSearch({})
        setCurrentPage(defaultParams.PAGE)
        setPageSize(defaultParams.LIMIT)
    }

    const handleOpenModalUpdate = (data: any) => {
        formUpdate.setFieldsValue(data)
        setModalUpdate({ isOpen: true, data })
    }

    const handleCloseModalUpdate = () => {
        setModalUpdate({ isOpen: false, data: null })
        formUpdate.resetFields()
    }

    const handleUpdateSimInventory = async (values: any) => {
        try {
            setIsLoading(true)
            await updateSimInventory({ simInventory: values })
            toastSuccess('Cập nhật đơn hàng thành công!')
            fetchData()
            handleCloseModalUpdate()
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra khi cập nhật!')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div>
            <ButtonBack />
            <h2>Mã đơn hàng: {orderId}</h2>
            <Form form={formSearch} onFinish={handleSearch}>
                <Space style={{ display: 'flex', alignItems: 'start' }}>
                    <Form.Item name='status' label='Trạng thái'>
                        <Select
                            style={{ width: 170 }}
                            showSearch
                            placeholder="Chọn trạng thái"
                            options={mbhStatusOptions}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    <Space>
                        <Button type='primary' htmlType="submit" icon={<SearchOutlined />}>Tìm kiếm</Button>
                        <Button onClick={handleClearSearch} icon={<UndoOutlined />}></Button>
                    </Space>
                </Space>
            </Form>
            <CustomTable
                namePage='giao dịch'
                columns={columns}
                dataSource={simInventory?.simInventory}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={simInventory?.total}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />
            <Modal
                title='Cập nhật kho sim'
                open={modalUpdate.isOpen}
                onCancel={handleCloseModalUpdate}
                footer={[]}
                width={600}
            >
                <Form
                    form={formUpdate}
                    onFinish={handleUpdateSimInventory}
                    layout="vertical"
                    autoComplete="off"
                    style={{ marginTop: 20 }}
                >
                    <Form.Item name='id' style={{ display: 'none' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                        <Form.Item name='cpCode' label='Công ty' style={{ flex: 1 }}>
                            <Select
                                showSearch
                                placeholder="Chọn công ty"
                                options={companyOptions}
                                filterOption={filterOptionByLabel}
                            />
                        </Form.Item>
                        <Form.Item name='status' label='Trạng thái' style={{ flex: 1 }}>
                            <Select
                                showSearch
                                placeholder="Chọn trạng thái"
                                options={mbhStatusOptions}
                                filterOption={filterOptionByLabel}
                            />
                        </Form.Item>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                        <Form.Item name='start_sim' label='Serial bắt đầu' style={{ flex: 1 }}>
                            <Input />
                        </Form.Item>
                        <Form.Item name='end_sim' label='Serial kết thúc' style={{ flex: 1 }}>
                            <Input />
                        </Form.Item>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                        <Form.Item name='total_sim' label='Tổng Sim' style={{ flex: 1 }}>
                            <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name='total_active' label='Tổng kích hoạt' style={{ flex: 1 }}>
                            <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name='type' label='Loại sim' style={{ flex: 1 }}>
                            <Select
                                showSearch
                                placeholder="Chọn loại sim"
                                options={simTypeOptions}
                                filterOption={filterOptionByLabel}
                            />
                        </Form.Item>
                    </div>
                    <Form.Item name='description' label='Mô tả'>
                        <Input.TextArea />
                    </Form.Item>
                    <div style={{ textAlign: 'right' }}>
                        <Button
                            htmlType='submit'
                            style={{ background: '#ff9800', color: 'white' }}>
                            Cập nhật
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    )
}
